import {useCallback, useEffect, useState} from 'react';
import {checkBackendHealth} from '../api/health/healthApiClient';

const useBackendHealth = () => {
    const [isBackendAlive, setIsBackendAlive] = useState(true); // Start optimistic
    const [isChecking, setIsChecking] = useState(false);
    const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

    /**
     * @param {boolean} silent  true → no loading-spinner flicker for
     *   background polls. The periodic 30s tick passes silent=true so
     *   App.js doesn't re-render five times per minute (which made
     *   controlled inputs feel like they were resetting mid-typing).
     *   Initial mount and the user's Retry button leave silent=false
     *   so the spinner shows.
     */
    const performHealthCheck = useCallback(async (silent = false) => {
        if (!silent) setIsChecking(true);
        try {
            const healthResult = await checkBackendHealth();
            // No-op setState when the value didn't change — saves the
            // re-render in the "everything is fine, ping succeeded"
            // path that runs 99% of the time.
            setIsBackendAlive((prev) =>
                prev === healthResult.isAlive ? prev : healthResult.isAlive
            );
            setHasCheckedOnce((prev) => (prev ? prev : true));
        } catch (error) {
            console.error('Health check failed:', error);
            setIsBackendAlive((prev) => (prev === false ? prev : false));
            setHasCheckedOnce((prev) => (prev ? prev : true));
        } finally {
            if (!silent) setIsChecking(false);
        }
    }, []);

    useEffect(() => {
        // Initial check — noisy (shows the loading state on first paint).
        performHealthCheck();
        // Background 30s poll — silent (no isChecking flip, no re-render
        // when status is unchanged).
        const intervalId = setInterval(() => performHealthCheck(true), 30000);
        return () => clearInterval(intervalId);
    }, [performHealthCheck]);

    return {
        isBackendAlive,
        isSystemHealthy: isBackendAlive,
        isChecking,
        hasCheckedOnce,
        performHealthCheck,
    };
};

export default useBackendHealth; 